import React, { useState, useEffect } from 'react'
import { getAllFenceConfigs, FenceConfig } from '../config/fenceConfigs'
import { tileModels, TileModel, TileColor, TileVariant } from '../utils/tileTextures'
import AddColorPopup from './AddColorPopup'

interface AdminPanelProps {
  onClose?: () => void
}

const AdminPanel: React.FC<AdminPanelProps> = ({ onClose }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [fenceConfigs, setFenceConfigs] = useState<FenceConfig[]>([])
  const [selectedFence, setSelectedFence] = useState<FenceConfig | null>(null)
  const [activeSection, setActiveSection] = useState<'fences' | 'visualizer'>('fences')
  const [activeFenceTab, setActiveFenceTab] = useState<'prices' | 'textures'>('prices')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [showAddColorPopup, setShowAddColorPopup] = useState(false)

  // Sprawdź czy użytkownik jest już zalogowany
  useEffect(() => {
    const savedAuth = localStorage.getItem('admin_authenticated')
    if (savedAuth === 'true') {
      setIsAuthenticated(true)
    }
    loadConfigs()
  }, [])

  const loadConfigs = async () => {
    try {
      setLoading(true)
      const configs = await getAllFenceConfigs()
      // 🎯 Filtruj konfiguracje - usuń te z variantConfigs (royal-mix, lupane-muro-mix)
      // bo one korzystają z pojedynczych konfiguracji dla cen i tekstur
      const filteredConfigs = configs.filter(config => !config.variantConfigs || Object.keys(config.variantConfigs).length === 0)
      setFenceConfigs(filteredConfigs)
    } catch (error) {
      console.error('❌ Błąd ładowania konfiguracji:', error)
      showMessage('error', 'Nie udało się załadować konfiguracji')
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    // Stałe hasło do panelu administracyjnego
    const adminPassword = 'KB26!4@6#'
    
    if (password === adminPassword) {
      setIsAuthenticated(true)
      localStorage.setItem('admin_authenticated', 'true')
      setPassword('')
    } else {
      showMessage('error', 'Nieprawidłowe hasło')
    }
  }

  const handleLogout = () => {
    setIsAuthenticated(false)
    localStorage.removeItem('admin_authenticated')
    setSelectedFence(null)
  }

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 3000)
  }

  const handleSave = async () => {
    if (!selectedFence) return

    try {
      setSaving(true)
      const response = await fetch(`/api/admin/save-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fenceId: selectedFence.id,
          config: selectedFence,
        }),
      })

      if (response.ok) {
        showMessage('success', 'Konfiguracja została zapisana')
        // Przeładuj konfiguracje z filtrem (ukryj konfiguracje z variantConfigs)
        const configs = await getAllFenceConfigs()
        const filteredConfigs = configs.filter(config => !config.variantConfigs || Object.keys(config.variantConfigs).length === 0)
        setFenceConfigs(filteredConfigs)
        // Zaktualizuj wybrane ogrodzenie
        const updated = filteredConfigs.find(f => f.id === selectedFence.id)
        if (updated) setSelectedFence(updated)
      } else {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Błąd zapisu')
      }
    } catch (error) {
      console.error('❌ Błąd zapisu:', error)
      showMessage('error', error instanceof Error ? error.message : 'Nie udało się zapisać konfiguracji')
    } finally {
      setSaving(false)
    }
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Panel Administracyjny</h1>
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Hasło
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Wprowadź hasło"
                autoFocus
              />
            </div>
            <button
              type="submit"
              className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              Zaloguj się
            </button>
          </form>
          {message && message.type === 'error' && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {message.text}
            </div>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="mt-4 w-full text-gray-600 hover:text-gray-800 transition-colors"
            >
              Powrót do aplikacji
            </button>
          )}
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Ładowanie konfiguracji...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Panel Administracyjny</h1>
              <div className="mt-3 flex space-x-2">
                <button
                  onClick={() => setActiveSection('fences')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium border ${
                    activeSection === 'fences'
                      ? 'bg-red-600 text-white border-red-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Ogrodzenia
                </button>
                <button
                  onClick={() => setActiveSection('visualizer')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium border ${
                    activeSection === 'visualizer'
                      ? 'bg-red-600 text-white border-red-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Wizualizator
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {message && (
                <div className={`px-4 py-2 rounded-lg ${
                  message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {message.text}
                </div>
              )}
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Wyloguj
              </button>
              {onClose && (
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Powrót
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Sekcja OGRODZENIA */}
        {activeSection === 'fences' && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar - Lista ogrodzeń */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Ogrodzenia</h2>
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {fenceConfigs.map((fence) => (
                    <button
                      key={fence.id}
                      onClick={() => setSelectedFence(fence)}
                      className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                        selectedFence?.id === fence.id
                          ? 'bg-red-100 text-red-700 font-medium'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {fence.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Tabs dla ogrodzeń: ceny / tekstury */}
              <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <div className="flex space-x-4 border-b border-gray-200 mb-6">
                  <button
                    onClick={() => {
                      setActiveFenceTab('prices')
                      if (!selectedFence && fenceConfigs.length > 0) {
                        setSelectedFence(fenceConfigs[0])
                      }
                    }}
                    className={`px-4 py-2 font-medium transition-colors ${
                      activeFenceTab === 'prices'
                        ? 'text-red-600 border-b-2 border-red-600'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Ceny
                  </button>
                  <button
                    onClick={() => {
                      setActiveFenceTab('textures')
                      if (!selectedFence && fenceConfigs.length > 0) {
                        setSelectedFence(fenceConfigs[0])
                      }
                    }}
                    className={`px-4 py-2 font-medium transition-colors ${
                      activeFenceTab === 'textures'
                        ? 'text-red-600 border-b-2 border-red-600'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Tekstury
                  </button>
                </div>
              </div>

              {/* Content ogrodzeń */}
              {selectedFence ? (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  {activeFenceTab === 'prices' ? (
                    <PricesEditor fence={selectedFence} onUpdate={(updated) => setSelectedFence(updated)} />
                  ) : (
                    <TexturesEditor 
                      fence={selectedFence} 
                      onUpdate={(updated) => setSelectedFence(updated)}
                      onAddColor={() => setShowAddColorPopup(true)}
                    />
                  )}

                  {/* Save Button */}
                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      {saving ? 'Zapisywanie...' : 'Zapisz zmiany'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                  <p className="text-gray-500">Wybierz ogrodzenie z listy po lewej stronie</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sekcja WIZUALIZATOR */}
        {activeSection === 'visualizer' && (
          <div className="grid grid-cols-1">
            <TilesEditor />
          </div>
        )}
      </div>

      {/* Add Color Popup */}
      {selectedFence && (
        <AddColorPopup
          isOpen={showAddColorPopup}
          onClose={() => setShowAddColorPopup(false)}
          onSave={async (colorData) => {
            const colors = [...(selectedFence.colors || []), colorData.colorName]
            const colorTextures = { ...selectedFence.colorTextures } || {}
            
            colorTextures[colorData.colorName] = {
              materialTexture: colorData.materialTexture || '',
              aluminumTexture: colorData.aluminumTexture || '',
              thumbnail: colorData.thumbnail || '',
              modelPath: colorData.modelPath || '',
            }

            const updated = {
              ...selectedFence,
              colors,
              colorTextures,
            }
            
            setSelectedFence(updated)
            showMessage('success', `Kolor "${colorData.colorName}" został dodany`)
          }}
          fenceId={selectedFence.id}
        />
      )}
    </div>
  )
}

// Komponent do edycji cen
interface PricesEditorProps {
  fence: FenceConfig
  onUpdate: (updated: FenceConfig) => void
}

const PricesEditor: React.FC<PricesEditorProps> = ({ fence, onUpdate }) => {
  const updatePrice = (field: 'blockPrice' | 'decorationBlockPrice', value: number) => {
    onUpdate({
      ...fence,
      [field]: value,
    })
  }

  const updateColorPrice = (color: string, field: 'blockPrice' | 'decorationBlockPrice', value: number) => {
    const colorPrices = { ...fence.colorPrices } || {}
    if (!colorPrices[color]) {
      colorPrices[color] = {}
    }
    colorPrices[color] = {
      ...colorPrices[color],
      [field]: value,
    }
    onUpdate({
      ...fence,
      colorPrices,
    })
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Ceny globalne</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cena bloczka zwykłego (zł)
            </label>
            <input
              type="number"
              step="0.01"
              value={fence.blockPrice || ''}
              onChange={(e) => updatePrice('blockPrice', parseFloat(e.target.value) || 0)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cena bloczka dekoracyjnego (zł)
            </label>
            <input
              type="number"
              step="0.01"
              value={fence.decorationBlockPrice || ''}
              onChange={(e) => updatePrice('decorationBlockPrice', parseFloat(e.target.value) || 0)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {fence.colors && fence.colors.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Ceny dla kolorów</h3>
          <div className="space-y-4 max-h-[500px] overflow-y-auto">
            {fence.colors.map((color) => {
              const colorPrice = fence.colorPrices?.[color] || {}
              return (
                <div key={color} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">{color}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cena bloczka zwykłego (zł)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={colorPrice.blockPrice || ''}
                        onChange={(e) => updateColorPrice(color, 'blockPrice', parseFloat(e.target.value) || 0)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder={fence.blockPrice?.toString() || '0'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cena bloczka dekoracyjnego (zł)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={colorPrice.decorationBlockPrice || ''}
                        onChange={(e) => updateColorPrice(color, 'decorationBlockPrice', parseFloat(e.target.value) || 0)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder={fence.decorationBlockPrice?.toString() || '0'}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// Komponent do edycji tekstur
interface TexturesEditorProps {
  fence: FenceConfig
  onUpdate: (updated: FenceConfig) => void
  onAddColor: () => void
}

const TexturesEditor: React.FC<TexturesEditorProps> = ({ fence, onUpdate, onAddColor }) => {
  const [uploadingFiles, setUploadingFiles] = useState<Record<string, boolean>>({})
  const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({})

  const updateTextures = (color: string, partial: Partial<{ materialTexture: string | string[], aluminumTexture: string, thumbnail: string, modelPath: string }>) => {
    const colorTextures = { ...(fence.colorTextures || {}) }
    if (!colorTextures[color]) colorTextures[color] = { materialTexture: '', aluminumTexture: '' }
    colorTextures[color] = { ...colorTextures[color], ...partial }
    onUpdate({ ...fence, colorTextures })
  }

  const removeMaterialVariant = (color: string, index: number) => {
    const current = fence.colorTextures?.[color]?.materialTexture
    const arr: string[] = Array.isArray(current) ? [...current] : current ? [current] : []
    arr.splice(index, 1)
    updateTextures(color, { materialTexture: arr.length === 1 ? arr[0] : arr.length === 0 ? '' : arr })
  }

  const handleFileUpload = async (color: string, field: 'materialTexture' | 'aluminumTexture', file: File) => {
    const uploadKey = `${color}-${field}`
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }))
    setUploadErrors(prev => ({ ...prev, [uploadKey]: '' }))
    try {
      const formData = new FormData()
      formData.append('fenceId', fence.id)
      formData.append('colorName', color)
      formData.append(field, file)
      const response = await fetch('/api/admin/upload-texture', { method: 'POST', body: formData })
      if (!response.ok) {
        const err = await response.json()
        throw new Error(err.error || 'Błąd przesyłania')
      }
      const data = await response.json()
      if (field === 'materialTexture') {
        const uploaded = data.files?.materialTexture
        if (uploaded) {
          const current = fence.colorTextures?.[color]?.materialTexture
          const arr: string[] = Array.isArray(current) ? [...current] : current ? [current] : []
          arr.push(uploaded)
          updateTextures(color, {
            materialTexture: arr.length === 1 ? arr[0] : arr,
            ...(data.files?.thumbnail ? { thumbnail: data.files.thumbnail } : {}),
          })
        }
      } else {
        const uploaded = data.files?.aluminumTexture
        if (uploaded) updateTextures(color, { aluminumTexture: uploaded })
      }
    } catch (error) {
      setUploadErrors(prev => ({ ...prev, [uploadKey]: error instanceof Error ? error.message : 'Błąd' }))
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }))
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Tekstury dla kolorów</h3>
        <button
          onClick={onAddColor}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
        >
          + Dodaj nowy kolor
        </button>
      </div>

      <div className="space-y-4 max-h-[600px] overflow-y-auto">
        {(fence.colors || []).map((color) => {
          const tex = fence.colorTextures?.[color] || { materialTexture: '', aluminumTexture: '' }
          const matArr: string[] = Array.isArray(tex.materialTexture)
            ? tex.materialTexture
            : tex.materialTexture ? [tex.materialTexture] : []

          return (
            <div key={color} className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-4">
                {color}
                {matArr.length > 1 && (
                  <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-normal">
                    MIX — {matArr.length} warianty
                  </span>
                )}
              </h4>
              <div className="grid grid-cols-1 gap-4">
                {/* Tekstura materiału */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tekstura materiału</label>
                  {matArr.length > 0 && (
                    <div className="space-y-1 mb-3">
                      {matArr.map((path, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                          <span className="text-xs text-gray-500 flex-1 truncate" title={path}>
                            {idx + 1}. {path.split('/').pop()}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeMaterialVariant(color, idx)}
                            className="text-red-500 hover:text-red-700 text-xs font-medium px-2 py-0.5 rounded hover:bg-red-50 transition-colors flex-shrink-0"
                          >
                            ✕ Usuń
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="space-y-1">
                    <label className="block text-xs text-gray-500">
                      {matArr.length === 0 ? 'Wybierz plik tekstury' : matArr.length === 1 ? '+ Dodaj drugi wariant (efekt MIX)' : `+ Dodaj ${matArr.length + 1}. wariant`}
                    </label>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      className={`w-full px-4 py-2 border rounded-lg text-sm cursor-pointer transition-colors focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                        matArr.length > 0 ? 'border-dashed border-blue-300 bg-blue-50 hover:bg-blue-100' : 'border-gray-300'
                      }`}
                      disabled={uploadingFiles[`${color}-materialTexture`]}
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) { handleFileUpload(color, 'materialTexture', file); e.target.value = '' }
                      }}
                    />
                    {uploadingFiles[`${color}-materialTexture`] && <p className="text-sm text-blue-600">Przesyłanie...</p>}
                    {uploadErrors[`${color}-materialTexture`] && <p className="text-sm text-red-600">{uploadErrors[`${color}-materialTexture`]}</p>}
                  </div>
                </div>

                {/* Tekstura aluminium */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tekstura aluminium</label>
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      disabled={uploadingFiles[`${color}-aluminumTexture`]}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload(color, 'aluminumTexture', f) }}
                    />
                    {uploadErrors[`${color}-aluminumTexture`] && <p className="text-sm text-red-600">{uploadErrors[`${color}-aluminumTexture`]}</p>}
                    {uploadingFiles[`${color}-aluminumTexture`] && <p className="text-sm text-blue-600">Przesyłanie...</p>}
                    {tex.aluminumTexture && !uploadingFiles[`${color}-aluminumTexture`] && (
                      <p className="text-xs text-gray-500">Aktualna: {tex.aluminumTexture}</p>
                    )}
                  </div>
                </div>

                {/* Miniatura */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Miniatura (generowana automatycznie)</label>
                  <input
                    type="text"
                    value={tex.thumbnail || ''}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                    placeholder="/thumbnails/... (generowana z tekstury materiału)"
                    readOnly
                  />
                  <p className="text-xs text-gray-500 mt-1">Miniatura jest automatycznie generowana z pierwszej tekstury materiału.</p>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}


// Komponent do zarządzania teksturami wizualizatora (płytki/kostka brukowa)
const TilesEditor: React.FC = () => {
  const [tiles, setTiles] = useState<TileModel[]>(tileModels)
  const [selectedModel, setSelectedModel] = useState<TileModel | null>(null)
  const [selectedColor, setSelectedColor] = useState<TileColor | null>(null)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [showAddModelPopup, setShowAddModelPopup] = useState(false)
  const [showAddColorPopup, setShowAddColorPopup] = useState(false)
  const [showAddVariantPopup, setShowAddVariantPopup] = useState(false)

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 3000)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const response = await fetch('/api/admin/save-tiles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tileModels: tiles }),
      })

      if (response.ok) {
        showMessage('success', 'Tekstury wizualizatora zostały zapisane')
        // Przeładuj stronę aby załadować nowe tekstury
        window.location.reload()
      } else {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Błąd zapisu')
      }
    } catch (error) {
      console.error('❌ Błąd zapisu:', error)
      showMessage('error', error instanceof Error ? error.message : 'Nie udało się zapisać tekstur')
    } finally {
      setSaving(false)
    }
  }

  const handleAddModel = (modelName: string) => {
    const newModel: TileModel = {
      id: modelName.toLowerCase().replace(/\s+/g, '-'),
      name: modelName,
      colors: []
    }
    setTiles([...tiles, newModel])
    setSelectedModel(newModel)
    setShowAddModelPopup(false)
  }

  const handleAddColor = (colorName: string) => {
    if (!selectedModel) return
    
    const newColor: TileColor = {
      id: `${selectedModel.id}-${colorName.toLowerCase().replace(/\s+/g, '-')}`,
      name: colorName,
      variants: []
    }
    
    const updatedTiles = tiles.map(model =>
      model.id === selectedModel.id
        ? { ...model, colors: [...model.colors, newColor] }
        : model
    )
    
    setTiles(updatedTiles)
    setSelectedColor(newColor)
    setShowAddColorPopup(false)
  }

  const handleAddVariant = async (variantName: string, textureFile: File) => {
    if (!selectedModel || !selectedColor) return

    try {
      // Upload pliku tekstury
      const formData = new FormData()
      formData.append('modelId', selectedModel.id)
      formData.append('colorId', selectedColor.id)
      formData.append('variantName', variantName)
      formData.append('textureFile', textureFile)

      const uploadResponse = await fetch('/api/admin/upload-tile-texture', {
        method: 'POST',
        body: formData,
      })

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json()
        throw new Error(errorData.error || 'Błąd przesyłania pliku')
      }

      const uploadData = await uploadResponse.json()
      const texturePath = uploadData.texturePath

      const newVariant: TileVariant = {
        id: `${selectedColor.id}-${variantName.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`,
        name: variantName,
        texturePath: texturePath,
        fileName: textureFile.name
      }

      const updatedTiles = tiles.map(model =>
        model.id === selectedModel.id
          ? {
              ...model,
              colors: model.colors.map(color =>
                color.id === selectedColor.id
                  ? { ...color, variants: [...color.variants, newVariant] }
                  : color
              )
            }
          : model
      )

      setTiles(updatedTiles)
      setShowAddVariantPopup(false)
      showMessage('success', 'Wariant został dodany')
    } catch (error) {
      console.error('❌ Błąd dodawania wariantu:', error)
      showMessage('error', error instanceof Error ? error.message : 'Nie udało się dodać wariantu')
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Tekstury Wizualizatora</h2>
        <div className="flex items-center space-x-4">
          {message && (
            <div className={`px-4 py-2 rounded-lg ${
              message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {message.text}
            </div>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {saving ? 'Zapisywanie...' : 'Zapisz zmiany'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista modeli */}
        <div className="lg:col-span-1">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Modele</h3>
              <button
                onClick={() => setShowAddModelPopup(true)}
                className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                + Dodaj
              </button>
            </div>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {tiles.map((model) => (
                <button
                  key={model.id}
                  onClick={() => {
                    setSelectedModel(model)
                    setSelectedColor(null)
                  }}
                  className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                    selectedModel?.id === model.id
                      ? 'bg-red-100 text-red-700 font-medium'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {model.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Lista kolorów */}
        <div className="lg:col-span-1">
          {selectedModel ? (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Kolory: {selectedModel.name}</h3>
                <button
                  onClick={() => setShowAddColorPopup(true)}
                  className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  + Dodaj
                </button>
              </div>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {selectedModel.colors.map((color) => (
                  <button
                    key={color.id}
                    onClick={() => setSelectedColor(color)}
                    className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                      selectedColor?.id === color.id
                        ? 'bg-red-100 text-red-700 font-medium'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {color.name} ({color.variants.length} wariantów)
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-gray-500">Wybierz model</p>
            </div>
          )}
        </div>

        {/* Lista wariantów */}
        <div className="lg:col-span-1">
          {selectedColor ? (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Warianty: {selectedColor.name}</h3>
                <button
                  onClick={() => setShowAddVariantPopup(true)}
                  className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  + Dodaj
                </button>
              </div>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {selectedColor.variants.map((variant) => (
                  <div
                    key={variant.id}
                    className="bg-white rounded-lg p-3 border border-gray-200"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">{variant.name}</span>
                    </div>
                    <img
                      src={variant.texturePath}
                      alt={variant.name}
                      className="w-full h-24 object-cover rounded border border-gray-300"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none'
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-2 truncate">{variant.texturePath}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-gray-500">Wybierz kolor</p>
            </div>
          )}
        </div>
      </div>

      {/* Popup dodawania modelu */}
      {showAddModelPopup && (
        <AddModelPopup
          isOpen={showAddModelPopup}
          onClose={() => setShowAddModelPopup(false)}
          onSave={handleAddModel}
        />
      )}

      {/* Popup dodawania koloru */}
      {showAddColorPopup && selectedModel && (
        <AddTileColorPopup
          isOpen={showAddColorPopup}
          onClose={() => setShowAddColorPopup(false)}
          onSave={handleAddColor}
        />
      )}

      {/* Popup dodawania wariantu */}
      {showAddVariantPopup && selectedModel && selectedColor && (
        <AddVariantPopup
          isOpen={showAddVariantPopup}
          onClose={() => setShowAddVariantPopup(false)}
          onSave={handleAddVariant}
        />
      )}
    </div>
  )
}

// Popup do dodawania modelu
interface AddModelPopupProps {
  isOpen: boolean
  onClose: () => void
  onSave: (modelName: string) => void
}

const AddModelPopup: React.FC<AddModelPopupProps> = ({ isOpen, onClose, onSave }) => {
  const [modelName, setModelName] = useState('')

  if (!isOpen) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (modelName.trim()) {
      onSave(modelName.trim())
      setModelName('')
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Dodaj nowy model</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nazwa modelu <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="np. Aprilia"
              required
              autoFocus
            />
          </div>
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Anuluj
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              Dodaj model
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Popup do dodawania koloru
interface AddTileColorPopupProps {
  isOpen: boolean
  onClose: () => void
  onSave: (colorName: string) => void
}

const AddTileColorPopup: React.FC<AddTileColorPopupProps> = ({ isOpen, onClose, onSave }) => {
  const [colorName, setColorName] = useState('')

  if (!isOpen) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (colorName.trim()) {
      onSave(colorName.trim())
      setColorName('')
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Dodaj nowy kolor</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nazwa koloru <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={colorName}
              onChange={(e) => setColorName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="np. A4 Czekolada"
              required
              autoFocus
            />
          </div>
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Anuluj
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              Dodaj kolor
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Popup do dodawania wariantu
interface AddVariantPopupProps {
  isOpen: boolean
  onClose: () => void
  onSave: (variantName: string, textureFile: File) => Promise<void>
}

const AddVariantPopup: React.FC<AddVariantPopupProps> = ({ isOpen, onClose, onSave }) => {
  const [variantName, setVariantName] = useState('')
  const [textureFile, setTextureFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!variantName.trim()) {
      setError('Nazwa wariantu jest wymagana')
      return
    }

    if (!textureFile) {
      setError('Plik tekstury jest wymagany')
      return
    }

    setError(null)
    setUploading(true)

    try {
      await onSave(variantName.trim(), textureFile)
      setVariantName('')
      setTextureFile(null)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Wystąpił błąd')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Dodaj nowy wariant</h2>
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nazwa wariantu <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={variantName}
              onChange={(e) => setVariantName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="np. Wariant 1"
              required
              disabled={uploading}
              autoFocus
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Plik tekstury <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={(e) => setTextureFile(e.target.files?.[0] || null)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              disabled={uploading}
              required
            />
            {textureFile && (
              <p className="text-sm text-green-600 mt-2">✓ Plik wybrany: {textureFile.name}</p>
            )}
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              disabled={uploading}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={uploading || !variantName.trim() || !textureFile}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {uploading ? 'Przesyłanie...' : 'Dodaj wariant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AdminPanel
